import { useEffect, useState } from 'react'

import type { MessageSummaryType } from './api.ts'

// O que é puro (nome, telefone, cidade, marcadores, janela) mudou para
// `@aeris/status-meta` na F308: o atendimento do celular fala igual.
export {
  contactCityOf,
  contactHasName,
  contactLabelOf,
  contactPhoneOf,
  contactSeedOf,
  formatRemaining,
  formatWaId,
  inboxTagsOf,
  windowStateFor,
  type InboxTag,
  type InboxTagKind,
} from '@aeris/status-meta'

/**
 * O vocabulário do inbox — F085.
 *
 * Estas funções viviam dentro de `routes/atendimento.tsx`, que tinha 1544
 * linhas e desenhava a tela inteira. Elas saíram porque agora TRÊS componentes
 * precisam das mesmas respostas ("como esta conversa se chama?", "que horas
 * mostro nesta linha?") e a alternativa era importar de um componente para
 * outro — o caminho curto para um ciclo de imports.
 *
 * Nada aqui desenha nada. É decisão de apresentação em forma de dado.
 */

export const CONVERSATIONS_PAGE_SIZE = 20
/** Generoso o bastante para não precisar paginar a thread no dia a dia. */
export const MESSAGES_PAGE_SIZE = 100
export const REFRESH_INTERVAL_MS = 7_000
/** Se o stream não abrir dentro deste prazo, cai para o polling. */
export const STREAM_CONNECT_TIMEOUT_MS = 5_000

/** Só para o texto "faltam Xh" contar sozinho, sem exigir um refetch. */
export const useNow = (intervalMs: number): Date => {
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const timer = window.setInterval(() => {
      setNow(new Date())
    }, intervalMs)
    return () => {
      window.clearInterval(timer)
    }
  }, [intervalMs])
  return now
}

/**
 * A hora na lista: só o relógio se foi hoje, só a data se foi antes (F083).
 *
 * "18/08/2026, 15:47" numa coluna estreita consome a largura toda e trunca o
 * nome do contato ao lado. O WhatsApp resolve assim há dez anos, e a razão é
 * a mesma: quem olha a lista quer saber QUANDO em termos grosseiros; a hora
 * exata está na conversa.
 */
export const shortTimeOf = (at: string): string => {
  const date = new Date(at)
  if (date.toDateString() === new Date().toDateString()) {
    return date.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    })
  }
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
}

/** A hora dentro da bolha — sempre o relógio, porque o dia é o separador quem diz. */
export const clockOf = (at: string): string =>
  new Date(at).toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  })

/**
 * O separador de data entre blocos de mensagem — "Hoje", "Ontem", ou a data
 * (F083).
 *
 * Devolve `null` quando a mensagem cai no mesmo dia que a anterior: o
 * separador marca a TROCA de dia, não cada mensagem. A primeira mensagem da
 * thread sempre ganha um, porque não há dia anterior com que comparar.
 *
 * Sem isto, uma conversa de duas semanas é uma parede de horas soltas: "15:47"
 * três vezes não diz se foram hoje ou em três terças diferentes.
 */
export const dateSeparatorFor = (
  message: MessageSummaryType,
  previous: MessageSummaryType | undefined
): string | null => {
  const at = new Date(message.occurredAt)
  if (previous !== undefined) {
    const before = new Date(previous.occurredAt)
    if (at.toDateString() === before.toDateString()) return null
  }

  const today = new Date()
  if (at.toDateString() === today.toDateString()) return 'Hoje'

  const yesterday = new Date(today)
  yesterday.setDate(today.getDate() - 1)
  if (at.toDateString() === yesterday.toDateString()) return 'Ontem'

  return at.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

/**
 * O painel da direita fica aberto? Lembrado por dispositivo (F083).
 *
 * Molde de `SIDEBAR_STORAGE_KEY` (`app-shell.tsx`): `localStorage`, por
 * dispositivo, sem ida ao servidor. Uma preferência de enquadramento pertence
 * à tela em que a pessoa está, não à conta dela — o mesmo usuário no tablet do
 * balcão e no monitor grande quer coisas diferentes.
 */
const PANEL_STORAGE_KEY = 'aeris.inbox-panel-open'

export const readPanelOpen = (): boolean =>
  window.localStorage.getItem(PANEL_STORAGE_KEY) === 'true'

export const writePanelOpen = (open: boolean): void => {
  window.localStorage.setItem(PANEL_STORAGE_KEY, String(open))
}

/**
 * Qual número o inbox está mostrando (F091). `null` é TODOS.
 *
 * Mesmo molde de `PANEL_STORAGE_KEY` acima, e pelo mesmo motivo: é preferência
 * de enquadramento, não de conta. Quem atende o número do suporte a manhã
 * inteira não quer reescolher a cada F5.
 *
 * `readInboxAccount` recebe as contas que EXISTEM e devolve `null` para um id
 * que não está entre elas. Um número desconectado, apagado, ou guardado por
 * outra instalação do produto no mesmo navegador deixaria a tela pedindo um
 * recorte impossível — lista vazia, sem erro, sem explicação, e o seletor
 * mostrando um nome que não existe. Voltar para "todos" é a única saída que
 * não exige a pessoa entender o que aconteceu.
 */
const ACCOUNT_STORAGE_KEY = 'aeris.inbox-account'

export const readStoredInboxAccount = (): string | null =>
  window.localStorage.getItem(ACCOUNT_STORAGE_KEY)

/**
 * O número que a tela vai de fato mostrar — derivado, nunca sincronizado.
 *
 * Pura de propósito: quem chama guarda a escolha crua em `useState` e passa a
 * lista de contas quando ela chega. Um `useEffect` que "corrigisse" o estado
 * ao carregar as contas seria exatamente o que a regra
 * `react-hooks/set-state-in-effect` proíbe, e a versão derivada não tem o
 * quadro intermediário em que a tela mostra uma coisa e o estado outra.
 *
 * `available === undefined` é "as contas ainda não chegaram", e aí o guardado
 * VALE: ele era válido quando foi escrito, e desconfiar dele nesse intervalo
 * faria a lista carregar todos os números e pular para um só um instante
 * depois — duas requisições e um susto para quem só recarregou a página.
 */
export const resolveInboxAccount = (
  stored: string | null,
  available: readonly { id: string }[] | undefined
): string | null => {
  if (stored === null) return null
  if (available === undefined) return stored
  return available.some((account) => account.id === stored) ? stored : null
}

export const writeInboxAccount = (accountId: string | null): void => {
  if (accountId === null) {
    window.localStorage.removeItem(ACCOUNT_STORAGE_KEY)
    return
  }
  window.localStorage.setItem(ACCOUNT_STORAGE_KEY, accountId)
}

/**
 * O valor com atraso — para a busca não virar uma requisição por tecla.
 *
 * 250ms é o intervalo que separa "ainda digitando" de "parei": abaixo disso
 * uma pessoa comum dispara três ou quatro buscas por palavra; acima, a lista
 * demora a responder e parece travada.
 */
export const useDebounced = <T>(value: T, delayMs: number): T => {
  const [settled, setSettled] = useState(value)

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSettled(value)
    }, delayMs)
    return () => {
      window.clearTimeout(timer)
    }
  }, [value, delayMs])

  return settled
}

export const SEARCH_DEBOUNCE_MS = 250
